import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Target, Thermometer, Sun } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StatusChip } from "@/components/PressureAnalysisPanel";
import { useT } from "@/lib/i18n";
import { TYRES, formatDate } from "@/lib/format";
import { STATUS_CLS, analyzeStint, condModel, fmtP, tyreLabel } from "@/lib/pressureTargets";

const dCls = (d, tol) => (d == null ? "text-slate-500" : d > tol ? "text-[#FF3B30]" : d < -tol ? "text-[#007AFF]" : "text-[#10B981]");
const dBg = (d, tol) => (d == null ? "border-[#282D3D]" : d > tol ? "border-[#FF3B30]/50 bg-[#FF3B30]/5" : d < -tol ? "border-[#007AFF]/50 bg-[#007AFF]/5" : "border-[#10B981]/50 bg-[#10B981]/5");

export const SectionTitle = ({ children, icon: Icon, right }) => (
  <div className="flex flex-wrap items-center gap-3 mb-3">
    <h2 className="font-heading uppercase font-bold text-xl flex items-center gap-2">{Icon ? <Icon className="w-5 h-5 text-[#10B981]" /> : <span className="w-1 h-5 bg-[#10B981]" />}{children}</h2>
    {right && <div className="ml-auto">{right}</div>}
  </div>
);

export const KartGrid = ({ render, testid }) => {
  const { t } = useT();
  return (
    <div data-testid={testid} className="relative grid grid-cols-2 gap-x-10 gap-y-3 px-2">
      <div className="absolute left-1/2 top-3 bottom-3 w-px bg-[#282D3D]" />
      <div className="col-span-2 text-center font-mono text-[9px] uppercase tracking-[0.3em] text-slate-600">▲ {t("front")}</div>
      {TYRES.map((p) => <div key={p} className="relative">{render(p)}</div>)}
    </div>
  );
};

export const PressureSummary = ({ rows, unit, target }) => {
  const { t } = useT();
  const tol = target?.tolerance ?? 0.05;
  const done = rows.filter((r) => r.a.overall !== "na");
  const count = (s) => done.filter((r) => r.a.overall === s).length;
  const ok = count("ok");
  const avgOf = (p) => {
    const ds = done.map((r) => r.a.wheels[p].delta).filter((d) => d != null);
    return ds.length ? ds.reduce((a, b) => a + b, 0) / ds.length : null;
  };
  const rate = done.length ? Math.round((ok / done.length) * 100) : null;
  return (
    <div data-testid="pressure-summary" className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
      <div className="bg-[#12141C] border border-[#282D3D] rounded-sm p-5 space-y-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />{t("tyre_target")}</div>
            <div data-testid="pressure-summary-target" className="font-heading font-bold text-4xl text-[#10B981] leading-none mt-1">{target ? fmtP(target.target, unit) : "—"}<span className="text-base text-slate-500 ml-2">{target ? `±${fmtP(target.tolerance, unit)} ${unit}` : ""}</span></div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("in_target_rate")}</div>
            <div data-testid="pressure-summary-rate" className="font-heading font-bold text-4xl leading-none mt-1">{rate != null ? `${rate}%` : "—"}<span className="font-mono text-xs text-slate-500 ml-1">{ok}/{done.length}</span></div>
          </div>
        </div>
        <div className="h-2 rounded-full bg-[#090A0F] overflow-hidden flex">
          {[["ok", "#10B981"], ["mixed", "#FFB800"], ["high", "#FF3B30"], ["low", "#007AFF"]].map(([s, c]) => (
            <div key={s} style={{ width: `${done.length ? (count(s) / done.length) * 100 : 0}%`, background: c }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {["ok", "high", "low", "mixed"].map((s) => <span key={s} data-testid={`pressure-summary-count-${s}`} className={`font-mono text-[11px] px-2 py-1 border rounded-sm ${STATUS_CLS[s]}`}>{t(`p_${s}`)} · {count(s)}</span>)}
        </div>
      </div>
      <div className="bg-[#12141C] border border-[#282D3D] rounded-sm p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">{t("avg_delta_wheel")}</div>
        <KartGrid testid="pressure-summary-kart" render={(p) => {
          const d = avgOf(p);
          return (
            <div className={`border rounded-sm px-4 py-3 ${dBg(d, tol)}`}>
              <div className="font-mono text-[10px] uppercase text-slate-400">{t(`tyre_${p}`)}</div>
              <div data-testid={`pressure-summary-avg-${p}`} className={`font-heading font-bold text-3xl leading-tight ${dCls(d, tol)}`}>{fmtP(d, unit, true)}</div>
              <div className="font-mono text-[10px] text-slate-500">{d == null ? "" : Math.abs(d) <= tol ? t("p_ok") : d > 0 ? t("p_high") : t("p_low")}</div>
            </div>
          );
        }} />
      </div>
    </div>
  );
};

const KIND_LABEL = { both: "model_both", track: "model_track", air: "model_air", mean: "model_mean" };

export const ConditionsCalculator = ({ stints, unit, target }) => {
  const { t } = useT();
  const used = stints;
  const last = [...used].sort((a, b) => `${a.date}${a.session.session_time || ""}`.localeCompare(`${b.date}${b.session.session_time || ""}`)).pop();
  const [track, setTrack] = useState(last?.track_temp ?? "");
  const [air, setAir] = useState(last?.air_temp ?? "");
  const models = useMemo(() => Object.fromEntries(TYRES.map((p) => [p, condModel(used, p)])), [used]);
  const kind = models.fl?.kind || models.fr?.kind;
  const temp = (label, Icon, value, onChange, testid) => (
    <label className="flex flex-col gap-1 font-mono text-[10px] uppercase text-slate-500">{label}
      <div className="relative"><Icon className="w-4 h-4 absolute left-2.5 top-3 text-[#FFB800]" />
        <Input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 pl-8 bg-[#090A0F] border-[#282D3D] font-mono text-lg rounded-sm" />
      </div>
    </label>
  );
  return (
    <section data-testid="conditions-calculator" className="bg-[#12141C] border border-[#10B981]/40 rounded-sm p-5 space-y-5">
      <SectionTitle icon={Calculator}>{t("calc_title")}</SectionTitle>
      <div className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_2fr] gap-3 items-end">
        {temp(t("air_temp_short"), Sun, air, setAir, "calc-air-input")}
        {temp(t("track_temp_short"), Thermometer, track, setTrack, "calc-track-input")}
        <p className="col-span-2 lg:col-span-1 font-mono text-[11px] text-slate-400 lg:pb-2.5">{t("calc_basis").replace("{n}", used.length)}</p>
      </div>
      {!target ? <p className="font-mono text-xs text-[#FFB800]">{t("calc_need_model")}</p> : !used.length ? <p data-testid="calc-no-data" className="font-mono text-xs text-[#FFB800]">{t("calc_no_data")}</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-5">
          <KartGrid testid="calc-kart" render={(p) => {
            const m = models[p];
            const g = m ? m.predict(track, air) : null;
            const lastCold = last ? analyzeStint(last, target).wheels[p].cold : null;
            const cold = g == null ? null : target.target - g;
            return (
              <div data-testid={`calc-wheel-${p}`} className="border border-[#10B981]/40 bg-[#10B981]/5 rounded-sm px-4 py-3">
                <div className="font-mono text-[10px] uppercase text-slate-400">{t(`tyre_${p}`)} · {t("pa_cold_next")}</div>
                <div data-testid={`calc-cold-${p}`} className="font-heading font-bold text-4xl leading-tight text-[#10B981]">{fmtP(cold, unit)}<span className="text-sm text-slate-500 ml-1">{unit}</span></div>
                <div className="font-mono text-[10px] text-slate-400">{t("pa_gain")} {fmtP(g, unit, true)}{lastCold != null && cold != null ? ` · ${t("vs_last")} ${fmtP(cold - lastCold, unit, true)}` : ""}</div>
              </div>
            );
          }} />
          <div className="space-y-3">
            <div data-testid="calc-model-info" className="bg-[#090A0F] border border-[#282D3D] rounded-sm p-3 font-mono text-xs text-slate-300 space-y-1">
              <div><span className="text-slate-500">{t("calc_model")}: </span>{t(KIND_LABEL[kind] || "model_mean")} · n={used.length}</div>
              {models.fl?.bt != null && <div><span className="text-slate-500">{t("track_short")}: </span>{fmtP(models.fl.bt, unit, true)} {unit}/°C</div>}
              {models.fl?.ba != null && <div><span className="text-slate-500">{t("air_short")}: </span>{fmtP(models.fl.ba, unit, true)} {unit}/°C</div>}
              {kind && kind !== "both" && <div data-testid="calc-model-ignored" className="text-[#FFB800]">{t(kind === "track" ? "calc_air_ignored" : kind === "air" ? "calc_track_ignored" : "calc_temps_ignored")}</div>}
              <p className="text-[10px] text-slate-500 pt-1">{t("calc_hint2")}</p>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("calc_used")}</div>
            <ul data-testid="calc-used-list" className="max-h-48 overflow-y-auto space-y-1">
              {used.map((s) => {
                const a = analyzeStint(s, target);
                return (
                  <li key={s.key} className="flex items-center gap-2 font-mono text-[11px] bg-[#090A0F] border border-[#282D3D] rounded-sm px-2.5 py-1.5">
                    <span className="text-white truncate flex-1">{s.session.name}{s.n === 2 ? " · S2" : ""}</span>
                    <span className="text-slate-500">{s.air_temp ?? "—"}° / {s.track_temp ?? "—"}°</span>
                    <StatusChip status={a.overall} />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
};

const Cell = ({ w, unit, tol }) => (
  <div className={`inline-flex flex-col items-end px-2 py-1 border rounded-sm ${dBg(w.delta, tol)}`}>
    <span className="text-white text-[12px]"><span className="text-[#60A5FA]">{fmtP(w.cold, unit)}</span><span className="text-slate-600"> → </span>{fmtP(w.hot, unit)}</span>
    {w.delta != null && <span className={`text-[10px] ${dCls(w.delta, tol)}`}>{fmtP(w.delta, unit, true)}</span>}
  </div>
);

export const PressureTable = ({ rows, unit }) => {
  const { t, lang } = useT();
  const groups = useMemo(() => {
    const m = new Map();
    [...rows].sort((a, b) => `${b.st.session.event_date}${b.st.date}${b.st.session.session_time || ""}`.localeCompare(`${a.st.session.event_date}${a.st.date}${a.st.session.session_time || ""}`))
      .forEach((r) => {
        const k = `${r.st.session.event_id}|${r.st.date}`;
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(r);
      });
    return [...m.entries()];
  }, [rows]);
  return (
    <div data-testid="pressure-sessions-table" className="bg-[#12141C] border border-[#282D3D] rounded-sm overflow-x-auto">
      <table className="w-full font-mono text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#090A0F]">
          <tr>{[t("sessions"), t("driver"), t("tyre_model_filter"), `${t("air_short")} / ${t("track_short")}`, `${t("hum_short")} / ${t("wind_short")}`, ...TYRES.map((p) => t(`tyre_${p}`)), t("status")].map((h, i) => <th key={h} className={`px-3 py-2.5 whitespace-nowrap ${i >= 5 && i < 9 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        {groups.map(([k, rs]) => (
          <tbody key={k}>
            <tr className="bg-[#181B26]"><td colSpan={10} className="px-3 py-1.5 font-heading uppercase tracking-wide text-sm text-[#FFB800]">{rs[0].st.session.event_name} <span className="font-mono text-[11px] text-slate-400 normal-case ml-2">{rs[0].st.date ? rs[0].st.date.split("-").reverse().join("/") : formatDate(rs[0].st.session.event_date, lang)}</span></td></tr>
            {rs.map(({ st, a }) => (
              <tr key={st.key} data-testid={`pressure-row-${st.key}`} className="border-t border-[#282D3D]/60 hover:bg-[#181B26] transition-colors duration-150">
                <td className="px-3 py-2"><Link to={`/events/${st.session.event_id}/sessions/${st.session.id}`} className="text-white hover:text-[#FF3B30] font-bold">{st.session.name}{st.n === 2 ? " · S2" : ""}</Link><div className="text-[10px] text-slate-500">{st.session.session_time || ""}</div></td>
                <td className="px-3 py-2 text-[#FF3B30]">{st.session.driver || "—"}</td>
                <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{tyreLabel(st.brand, st.model) || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{st.air_temp ?? "—"}° / {st.track_temp ?? "—"}°</td>
                <td data-testid={`pressure-row-humwind-${st.key}`} className="px-3 py-2 whitespace-nowrap text-[#60A5FA]">{st.humidity != null ? `${st.humidity}%` : "—"} / {st.wind_speed != null ? `${st.wind_speed} km/h` : "—"}</td>
                {TYRES.map((p) => <td key={p} className="px-3 py-2 text-right"><Cell w={a.wheels[p]} unit={unit} tol={a.tol} /></td>)}
                <td className="px-3 py-2"><StatusChip status={a.overall} testid={`pressure-row-status-${st.key}`} /></td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
};

export const analyzeRows = (stints, resolveTarget) => stints.map((st) => ({ st, a: analyzeStint(st, resolveTarget(st)) }));
